import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CreateEventDialogComponent } from '@shared/dialogs/create-event-dialog/create-event-dialog.component';
import { CreateTeamDialogComponent } from '@shared/dialogs/create-team-dialog/create-team-dialog.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AppConstants } from '@core/constants';
import { AppBaseComponent } from '../app-component-base';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AuthService } from "@core/services/auth/auth.service";
import { FeatureSlugEnum } from '@shared/enums/feature-slug.enum';
import { FeatureManagementService } from '@shared/services/feature-management/feature-management.service';
import { HelperService } from "@shared/services/helper.service";
import {
  ManageAthleteDetailDialogComponent
} from "@shared/components/manage-athlete-detail-dialog/manage-athlete-detail-dialog.component";
import { TestTypeStorageService } from "@shared/services/test-type-storage/test-type-storage.service";
import { SessionService } from "@core/services/session.service";
import { OnboardingService } from "@shared/services/onboarding/onboarding.service";

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent extends AppBaseComponent {
  private authService: AuthService = inject(AuthService);
  private helperService: HelperService = inject(HelperService);
  private testTypeStorageService: TestTypeStorageService = inject(TestTypeStorageService);
  public sessionService: SessionService = inject(SessionService);
  private onboardingService: OnboardingService = inject(OnboardingService);

  public filteredOptions: Observable<string[]> | undefined;
  public country: any;
  public themeSwitcher: FormControl<any> = new UntypedFormControl(true);
  public myControl: UntypedFormControl = new UntypedFormControl();
  public coaches: any;
  public searchText: string = '';
  public isDarkMode: boolean = false;
  public isProductionEnvironment: boolean = this.helperService.isProductionEnvironment(location, AppConstants.ENVIRONMENT_URL.PROD);
  public showOldVersion: boolean = false;
  public isOnboardingComplete$: BehaviorSubject<boolean> = this.onboardingService.getOnboardingStatus();


  @Input() public currentTestId: string = '';
  @Output() public menuBarChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild(MatAutocompleteTrigger) public searchAutoComplete: | MatAutocompleteTrigger | undefined;
  @ViewChild('inputSearchText') public inputSearchText: | ElementRef<HTMLElement> | undefined;
  public v1Link: string = this.constant.V1_URL;
  public options: string[] = [
    'Search in athletes',
    'Search in teams',
    'Search in tests',
  ];

  constructor(
    private featureManagementService: FeatureManagementService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private destroyRef: DestroyRef,
  ) {
    super();
  }

  public onInitPage(): void {
    this.activatedRoute.queryParams.subscribe({
      next: (params: Params) => {
        if (params.hasOwnProperty('old') && params.old === 'true') {
          this.showOldVersion = true;
        }
      }
    });

    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );
    this.setDefaultTheme();
    this.handleReloadPage();
  }

  public openNewAthleteCreationPopup(): void {
    this.dialog.open(ManageAthleteDetailDialogComponent, {
      width: '800px',
      panelClass: 'general-dialog',
      autoFocus: false,
      disableClose: true,
      data: { isShowDetail: true },
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res): void => {
        if (res) {
          this.snackBar.open(AppConstants.NOTIFICATIONS.ATHLETE_ADDED, 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
          this.router.navigate(['home', 'manage', 'athlete']);
        }
      });
  }

  public openAthleteModalWindow(): void {
    this.openNewAthleteCreationPopup();
  }

  public openTeamModalWindow(): void {
    this.dialog.open(CreateTeamDialogComponent, {
      width: '600px',
      panelClass: 'general-dialog',
      autoFocus: false,
      disableClose: true,
      data: { isShowDetail: true },
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res): void => {
        if (res) {
          this.snackBar.open(AppConstants.NOTIFICATIONS.TEAM_ADDED, 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
          this.router.navigate(['home', 'tac', 'teams']);
        }
      });
  }

  public openEventModalWindow(): void {
    this.dialog.open(CreateEventDialogComponent, {
      width: '600px',
      panelClass: 'general-dialog',
      autoFocus: false,
      disableClose: true,
      data: { isShowDetail: true },
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res): void => {
        if (res) {
          this.snackBar.open(AppConstants.NOTIFICATIONS.EVENT_ADDED, 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
          this.router.navigate(['home', 'dynamic-simulation', 'manage-event']);
        }
      });
  }

  private handleReloadPage(): void {
    const self = this;

    document.addEventListener('visibilitychange', (): void => {
      if (document.hidden) {
        setTimeout((): void => {
          const theme: string | null = localStorage.getItem('theme');
          const valueControl = self.themeSwitcher?.value;
          if (
            (theme === AppConstants.THEME.DARK && !valueControl) ||
            (theme === AppConstants.THEME.LIGHT && valueControl)
          ) {
            window.location.reload();
          }
        }, 5000);
      }
    });
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    this.options.filter((option: string): boolean => {
      return option.toLowerCase().indexOf(filterValue) === 0;
    });
    return this.options.filter(
      (option: string): boolean => option.toLowerCase().indexOf(filterValue) === 0
    );
  }

  public logout(): void {
    this.authService.logout().subscribe();
  }

  public openHelpCenterLink(): void {
    window.open(AppConstants.HELP_CENTER.URL, '_blank');
  }

  private setDefaultTheme(): void {
    const theme: string | null = localStorage.getItem('theme');
    if (!theme) {
      localStorage.setItem('theme', AppConstants.THEME.DARK);
    }
    if (theme === AppConstants.THEME.DARK || !theme) {
      this.themeSwitcher.setValue(true);
      this.customRendererService.removeClass('body', ['light-theme']);
      this.customRendererService.addClass('body', ['dark-theme']);
      this.isDarkMode = true;
    } else if (theme === AppConstants.THEME.LIGHT) {
      this.themeSwitcher.setValue(false);
      this.customRendererService.removeClass('body', ['dark-theme']);
      this.customRendererService.addClass('body', ['light-theme']);
      this.isDarkMode = false;
    }
  }

  public changeTheme(): void {
    if (this.themeSwitcher?.value) {
      localStorage.setItem('theme', AppConstants.THEME.DARK);
    } else {
      localStorage.setItem('theme', AppConstants.THEME.LIGHT);
    }
    window.location.reload();
  }

  public toggleMenuBar(): void {
    this.menuBarChange.emit(true);
  }

  public navigateToTests(pathValue: string): void {
    let queryParams: { create: string; test_id?: string } = {
      create: pathValue,
    };
    if (pathValue == 'virtual') {
      queryParams = {
        ...queryParams,
        test_id: this.currentTestId,
      };
    }
    this.router.navigate(['home/manage', 'tests'], { queryParams });
  }

  public openDialog(isManual: boolean, isPPD: boolean, isVirtual: boolean, isLactate: boolean): void {
    this.navigateToNewTestCreationPage(isManual, isPPD, isVirtual, isLactate);
  }

  public navigateToNewTestCreationPage(isManual: boolean, isPPD: boolean, isVirtual: boolean, isLactate: boolean): void {
    this.testTypeStorageService.resetConfig();
    this.testTypeStorageService.setTestConfig({ isManual, isPPD, isVirtual, isLactate, testId: this.currentTestId });

    if (isManual) {
      this.router.navigate(['/home/manual-test']);
    } else if (isPPD) {
      this.router.navigate(['/home/ppd-test']);
    } else if (isVirtual) {
      this.router.navigate(['/home/virtual-test']);
    } else if (isLactate) {
      this.router.navigate(['/home/lactate-test']);
    }
  }

  public openManualTest(): void {
    this.openDialog(true, false, false, false);
  }

  public canCreatePPDTest(): boolean {
    return this.featureManagementService.getFeatureValue(FeatureSlugEnum.PPD);
  }

  public openPPDTest(): void {
    this.openDialog(false, true, false, false);
  }

  public openVirtualTest(): void {
    this.openDialog(false, false, true, false);
  }

  public canCreateLactateTest(): boolean {
    return this.featureManagementService.getFeatureValue(FeatureSlugEnum.LACTATE);
  }

  public openLactateTest(): void {
    this.openDialog(false, false, false, true);
  }

  public canSeeTestDropdown(): boolean {
    if (this.auth.role.is_owner) {
      return false;
    }

    const canCreateManualTest = this.rights.test_manual_test;
    const canCreateVirtualTest = this.rights.test_create_virtual;
    const canCreatePPDTest = this.canCreatePPDTest();
    const canCreateLactateTest = this.canCreateLactateTest();

    return canCreateManualTest || canCreateVirtualTest || canCreatePPDTest || canCreateLactateTest;
  }

  public isShowCreateButton() {
    return (
      (this.rights.athletes_create && !this.auth.role.is_owner) ||
      (this.rights.teams_list &&
        !this.auth.role.is_owner &&
        !this.auth.role.is_agent_role) ||
      (this.rights.dynamic_simulation_event_list &&
        !this.auth.role.is_owner &&
        !this.auth.role.is_agent_role) ||
      this.canSeeTestDropdown()
    );
  }
}
